import type { OperationsStatsForTeamLeaderResponse } from '@lyka-pet-food/lyka-shared-types/lib/types/FulfilmentService/operationsStats.js'
import { z } from 'zod'
import { Brand } from '../../shared'
import {
  BeCoolDepotCode,
  CreateShipmentResponse,
  CustomerDetails,
  DeliveryPreference,
  PostcodeFacilityResponse,
  Shipment,
} from '../courier'

export const MEAL_TYPE_OPTIONS = ['beef', 'chicken', 'kangaroo', 'lamb', 'pork', 'turkey', 'goat'] as const
export const MealType = z.enum(MEAL_TYPE_OPTIONS)
export type MealType = z.infer<typeof MealType>

export const JobStatusSchema = z.enum([
  'pending',
  'deprioritised',
  'backlog',
  'queued',
  'in_progress',
  'packed',
  'palletised',
  'paused',
  'unpaused',
  'cancelled',
  'on_hold',
  'delivered',
])

export type JobStatusType = z.infer<typeof JobStatusSchema>

export const ExtrasCategoryType = z.enum(['treat', 'supplement', 'collateral', 'merch']).describe('Extras category')

export const BoxItemBase = z
  .object({
    sku: z.string().describe('Item SKU'),
    name: z.string().describe('Item name'),
    productCode: z.string().optional().describe('Product Code'),
    productVariantName: z.string().describe('Product Variant name'),
    productVariantId: z.string().optional().describe('Product Variant ID'),
    quantity: z.number().describe('Item Quantity'),
    barcode: z.string().describe('Item barcode'),
  })
  .describe('Box Item to be packed')

export const BoxItemMeal = BoxItemBase.extend({
  type: z.literal('meal').describe('Item type: Meal'),
  weight: z.number().describe('Item weight'),
}).describe('Box Item to be packed - meal')

export const BoxItemExtra = BoxItemBase.extend({
  type: z.literal('extra').describe('Item type: Extra'),
  category: ExtrasCategoryType,
}).describe('Box Item to be packed - extra')

export const BoxItemIcePackBottom = BoxItemBase.extend({
  type: z.literal('icepack_bottom').describe('Item type: Icepack Bottom'),
  weight: z.number().describe('Item weight'),
}).describe('Box Item to be packed - ice pack bottom')

export const BoxItemIcePackTop = BoxItemBase.extend({
  type: z.literal('icepack_top').describe('Item type: Icepack Top'),
  weight: z.number().describe('Item weight'),
}).describe('Box Item to be packed - ice pack top')

export const BoxItem = z.discriminatedUnion('type', [
  BoxItemMeal,
  BoxItemExtra,
  BoxItemIcePackTop,
  BoxItemIcePackBottom,
])

export const LineItemBase = z
  .object({
    productVariantId: z.string().describe('Product Variant ID'),
    lineItemId: z.string().describe('Item ID'),
    name: BoxItemBase.shape.name,
    barcode: BoxItemBase.shape.barcode,
    quantity: BoxItemBase.shape.quantity,
    productCode: z.string().describe('Product Code'),
    productVariantSku: BoxItemBase.shape.sku,
    productVariantName: z.string().describe('Product Variant name'),
    width: z.number().describe('Item Width'),
    height: z.number().describe('Item Height'),
    depth: z.number().describe('Item Depth'),
    weight: z.number().describe('Item Weight'),
    volumetricWeight: z.number().describe('Item Volumetric Weight'),
  })
  .describe('Line Item to be fulfilled')

export const LineItemMeal = LineItemBase.extend({
  type: BoxItemMeal.shape.type,
}).describe('Line Item to be fulfilled - Meal')
export type LineItemMeal = z.infer<typeof LineItemMeal>

export const LineItemExtra = LineItemBase.extend({
  type: BoxItemExtra.shape.type,
  category: BoxItemExtra.shape.category,
}).describe('Line Item to be fulfilled - Extra')
export type LineItemExtra = z.infer<typeof LineItemExtra>

export const LineItemIcePackBottom = LineItemBase.extend({
  type: z.literal('icepack_bottom').describe('Item type: Icepack Bottom'),
}).describe('Line Item to be fulfilled - ice pack')
export type LineItemIcePackBottom = z.infer<typeof LineItemIcePackBottom>

export const LineItemIcePackTop = LineItemBase.extend({
  type: z.literal('icepack_top').describe('Item type: Icepack Top'),
}).describe('Line Item to be fulfilled - ice pack')
export type LineItemIcePackTop = z.infer<typeof LineItemIcePackTop>

export const LineItem = z.discriminatedUnion('type', [
  LineItemMeal,
  LineItemExtra,
  LineItemIcePackBottom,
  LineItemIcePackTop,
])

export type LineItem = z.infer<typeof LineItem>

export const Order = z
  .object({
    customer: CustomerDetails,
    orderId: z.string().describe('Order ID'),
    fulfilmentOrderId: z.string().describe('Fulfilment Order ID'),
    items: z.array(LineItem),
    deliveryPreference: DeliveryPreference,
  })
  .describe('Order to be fulfilled')
export type Order = z.infer<typeof Order>

export const FulfilInput = z.array(Order).min(1).describe('Fulfil Input')
export type FulfilInput = z.infer<typeof FulfilInput>

export const Box = z.object({
  id: z.string(),
  width: z.number(),
  height: z.number(),
  depth: z.number(),
  maxWeight: z.number(),
  maxKg: z.number(),
  boxItems: z.array(LineItem),
})

export type Box = z.infer<typeof Box>

export const AssignFacilityWorkflowInput = z.object({
  input: FulfilInput,
  groupedBoxes: z.object({
    boxes: z.array(Box),
  }),
  delivery: CreateShipmentResponse.shape.delivery,
  facility: PostcodeFacilityResponse,
})
export type AssignFacilityWorkflowInput = z.infer<typeof AssignFacilityWorkflowInput>

export const FulfilmentBox = z.object({
  boxId: z.string(),
  boxSerialNumber: z.string(),
  orderId: z.string(),
  contents: z.array(BoxItem),
})
export type FulfilmentBox = z.infer<typeof FulfilmentBox>

export const MealPlanSnapshotRecipeSchema = z.object({
  protein: z.string().min(1),
  quantity: z.number().int().nonnegative(),
})
export type MealPlanSnapshotRecipe = z.infer<typeof MealPlanSnapshotRecipeSchema>

export const MealPlanSnapshotDogSchema = z.object({
  animalId: z.string().uuid(),
  name: z.string(),
  dailyServingGrams: z.number().nonnegative(),
  pouchSizeGrams: z.number().int().positive(),
  pouchesPerDay: z.number().positive(),
  recipes: z.array(MealPlanSnapshotRecipeSchema),
})
export type MealPlanSnapshotDog = z.infer<typeof MealPlanSnapshotDogSchema>

/** Persisted at fulfilment create for Meal Plan print (Phoenix customers). Extras are not stored. */
export const MealPlanSnapshotSchema = z.object({
  title: z.string(),
  dogs: z.array(MealPlanSnapshotDogSchema).min(1),
})
export type MealPlanSnapshot = z.infer<typeof MealPlanSnapshotSchema>

export const FulfilmentInput = z.object({
  boxes: z.array(FulfilmentBox),
  customerId: z.string(),
  shipmentId: z.string(),
  // TODO: alter this to DepotCode when we have multiple couriers
  depotCode: BeCoolDepotCode,
  makeDate: Shipment.shape.makeDate,
  dispatchDate: Shipment.shape.dispatchDate,
  customerName: z.string(),
  mealPlanSnapshot: MealPlanSnapshotSchema.optional().nullable(),
})
export type FulfilmentInput = z.infer<typeof FulfilmentInput>

export const AssignFacilitySchema = FulfilmentInput.omit({ depotCode: true }).extend({
  postcode: z.string().min(4),
})

export type AssignFacilityInput = z.infer<typeof AssignFacilitySchema>
export type AssignFacilityResponse = z.infer<typeof AssignFacilitySchema>
export type BoxItemBase = z.infer<typeof BoxItemBase>
export type BoxItem = z.infer<typeof BoxItem>

// TODO: BoxResponseSchema is out of date — it doesn't match the actual response from either
// endpoint that uses it. `getBoxesByOrderId` (jobs.controller.ts#getJobsByOrderId) returns a
// richer shape with nested `job` and full `fulfilment` objects (see GetBoxesByOrderResponseSchema),
// while `updateBoxSerialNumber` returns a flat shape with `jobId`, `facilityId`,
// `boxingStationId`, `queuePosition`. Fix: replace BoxResponseSchema usage in the contract
// with endpoint-specific response schemas.
export const BoxResponseSchema = FulfilmentBox.extend({
  fulfilment: z.object({
    fulfilmentId: z.string().uuid(),
  }),
})

export type BoxResponseDto = z.infer<typeof BoxResponseSchema>

/*******************************************************************************
 * GetBoxesByOrderId response — matches the actual shape returned by
 * jobs.controller.ts#getJobsByOrderId (GET /api/v1/jobs/by-order/:orderId)
 ******************************************************************************/
export const GetBoxesByOrderFulfilmentSchema = z.object({
  fulfilmentId: z.string(),
  customerId: z.string(),
  shipmentId: z.string(),
  boxIds: z.array(z.string()),
  makeDate: z.string().nullable(),
  depotCode: z.string(),
  facilityId: z.number(),
  facilityName: z.string().nullable(),
  isBacklogged: z.boolean(),
  brand: z.nativeEnum(Brand),
  createdTimeUTC: z.string(),
  updatedTimeUTC: z.string(),
})

export type GetBoxesByOrderFulfilment = z.infer<typeof GetBoxesByOrderFulfilmentSchema>

export const GetBoxesByOrderJobSchema = z.object({
  createdTimeUTC: z.string(),
  updatedTimeUTC: z.string(),
  jobId: z.number(),
  fulfilmentId: z.string(),
  facilityId: z.number(),
  boxingStationId: z.number().int().nullable(),
  boxId: z.string(),
  boxSerialNumber: z.string(),
  status: JobStatusSchema,
  pnpWorkerName: z.string().nullable(),
  palletId: z.string().nullable(),
})

export type GetBoxesByOrderJob = z.infer<typeof GetBoxesByOrderJobSchema>

export const GetBoxesByOrderResponseSchema = z.object({
  createdTimeUTC: z.string(),
  updatedTimeUTC: z.string(),
  boxId: z.string(),
  serialNumber: z.string(),
  orderId: z.string().nullable(),
  has5pmCutoff: z.boolean(),
  customerName: z.string().nullable(),
  dispatchTimeUTC: z.string(),
  contents: z.array(BoxItem),
  job: GetBoxesByOrderJobSchema,
  fulfilment: GetBoxesByOrderFulfilmentSchema,
})

export type GetBoxesByOrderResponse = z.infer<typeof GetBoxesByOrderResponseSchema>

export const JobSchema = z.object({
  jobId: z.number(),
  fulfilmentId: z.string(),
  facilityId: z.number(),
  boxId: z.string(),
  boxSerialNumber: z.string(),
  contents: z.array(BoxItem),
  orderId: z.string().nullable(),
  status: JobStatusSchema,
  createdTimeUTC: z.string(),
  updatedTimeUTC: z.string().optional(),
  customerId: z.string().optional(),
  customerName: z.string().optional(),
  shipmentId: z.string().optional(),
  stationId: z.number().int().optional(),
  boxingStationId: z.number().int().optional(),
  pnpWorkerId: z.string().optional(),
  brand: z.string().optional(),
})

export type FulfilmentJob = z.infer<typeof JobSchema>

export const HasStartedFulfilmentSchema = z.object({
  hasStarted: z.boolean(),
  startedJobsCount: z.number(),
  totalJobsCount: z.number(),
})

export type HasStartedFulfilment = z.infer<typeof HasStartedFulfilmentSchema>

export const ToggleHoldStatusSchema = z.object({
  isOnHold: z.boolean(),
})

export type ToggleHoldStatus = z.infer<typeof ToggleHoldStatusSchema>

export const FulfilmentBoxDetailsSchema = z.object({
  createdTimeUTC: z.string(),
  updatedTimeUTC: z.string(),
  boxId: z.string(),
  serialNumber: z.string(),
  orderId: z.string().nullable(),
  has5pmCutoff: z.boolean(),
  customerName: z.string(),
  dispatchTimeUTC: z.string(),
  contents: z.array(BoxItem),
})

export type FulfilmentBoxDetails = z.infer<typeof FulfilmentBoxDetailsSchema>

export const FulfilmentSchema = z.object({
  fulfilmentId: z.string(),
  makeDate: z.string(),
  customerId: z.string(),
  depotCode: z.string(),
  createdTimeUTC: z.string(),
  updatedTimeUTC: z.string(),
  shipmentId: z.string().optional(),
  facilityId: z.number(),
  isOnHold: z.boolean().optional(),
  isBacklogged: z.boolean().optional(),
  boxIds: z.array(z.string()).optional(),
  jobs: z.array(JobSchema).optional(),
  boxes: z.array(FulfilmentBoxDetailsSchema).optional(),
})

export type Fulfilment = z.infer<typeof FulfilmentSchema>

export const RequeueJobsResponseSchema = z.object({
  queuedJobs: z.array(z.number()),
  skippedJobs: z.array(z.number()),
  message: z.string(),
})

export type RequeueJobsResponse = z.infer<typeof RequeueJobsResponseSchema>

export const UpdateFulfilmentSchema = z.object({
  customerId: z.string().optional(),
  shipmentId: z.string().optional(),
  depotCode: BeCoolDepotCode.optional(),
  makeDate: Shipment.shape.makeDate.optional(),
  facilityId: z.number().int().optional(),
  isBacklogged: z.boolean().optional(),
  boxIds: z.array(z.string()).optional(),
  mealPlanSnapshot: MealPlanSnapshotSchema.optional().nullable(),
})

export type UpdateFulfilment = z.infer<typeof UpdateFulfilmentSchema>

export const UpdateBoxSerialNumberSchema = z.object({
  invoiceNumber: z
    .string()
    .min(1)
    .regex(/^[A-Z0-9]+$/i, 'Invoice number must contain only letters and numbers')
    .describe('The new invoice number for the box serial number'),
})

export type UpdateBoxSerialNumber = z.infer<typeof UpdateBoxSerialNumberSchema>

export const UpdateBoxContentsRequestSchema = z.object({
  contents: z
    .array(BoxItem)
    .min(1, 'Box contents cannot be empty')
    .describe('New contents for the box'),
  reason: z
    .string()
    .min(1, 'Reason is required')
    .max(500, 'Reason must be 500 characters or less')
    .describe('Reason for updating box contents'),
  updatedBy: z
    .string()
    .min(1, 'Updated by is required')
    .max(100, 'Updated by must be 100 characters or less')
    .describe('Who is updating the box contents'),
})

export type UpdateBoxContentsRequest = z.infer<typeof UpdateBoxContentsRequestSchema>

export const UpdateBoxContentsResponseSchema = z.object({
  success: z.boolean().describe('Whether the update was successful'),
  boxId: z.string().uuid().describe('ID of the updated box'),
  updatedContents: z.array(BoxItem).describe('The new contents of the box'),
  message: z.string().describe('Success or error message'),
})

export type UpdateBoxContentsResponse = z.infer<typeof UpdateBoxContentsResponseSchema>

export const JobPatchSchema = z.object({
  dispatchTimeUTC: z.string().nullable().optional(),
  customerName: z.string().nullable().optional(),
}).passthrough()

export type JobPatch = z.infer<typeof JobPatchSchema>

export const BoxArrangementBoxInputSchema = z.object({
  boxId: z.string().uuid(),
  boxSerialNumber: z.string(),
  orderId: z.string(),
  contents: z.array(BoxItem),
})

export type BoxArrangementBoxInput = z.infer<typeof BoxArrangementBoxInputSchema>

export const BoxArrangementInputSchema = z.object({
  boxes: z.array(BoxArrangementBoxInputSchema),
})

export type BoxArrangementInput = z.infer<typeof BoxArrangementInputSchema>

export const BoxArrangementResultSchema = z.object({
  success: z.boolean(),
  fulfilmentId: z.string(),
  previousBoxCount: z.number(),
  newBoxCount: z.number(),
  cancelledJobIds: z.array(z.number()),
  createdJobIds: z.array(z.number()),
  createdBoxIds: z.array(z.string()),
  message: z.string(),
})

export type BoxArrangementResult = z.infer<typeof BoxArrangementResultSchema>

export const MealPlanMealItemSchema = z.object({
  name: z.string(),
  quantity: z.number(),
})
export type MealPlanMealItem = z.infer<typeof MealPlanMealItemSchema>

export const MealPlanMealSchema = z.object({
  weight: z.number(),
  items: z.array(MealPlanMealItemSchema),
})
export type MealPlanMeal = z.infer<typeof MealPlanMealSchema>

export const MealPlanExtrasSchema = z.object({
  quantity: z.number(),
  name: z.string(),
  variantName: z.string().optional(),
})
export type MealPlanExtras = z.infer<typeof MealPlanExtrasSchema>

export const MealPlanInputSchema = z.object({
  boxId: z.string(),
  boxSerialNumber: z.string().optional(),
  meals: z.array(MealPlanMealSchema),
  extras: z.array(MealPlanExtrasSchema),
})
export type MealPlanInput = z.infer<typeof MealPlanInputSchema>

export const MealPlanPDFSchema = z.object({
  base64EncodedPDF: z.string(),
})
export type MealPlanPDF = z.infer<typeof MealPlanPDFSchema>

export { OperationsStatsForTeamLeaderResponse as OperationsStatsTeamLeaderSchema } from '@lyka-pet-food/lyka-shared-types/lib/types/FulfilmentService/operationsStats.js'

export { BoxWithTransformedContentsResponse as BoxTrackerBoxSchema } from '@lyka-pet-food/lyka-shared-types/lib/types/FulfilmentService/operationsStats.js'

export type OperationsStatsTeamLeader = z.infer<typeof OperationsStatsForTeamLeaderResponse>

/*******************************************************************************
 * Operations Stats
 ******************************************************************************/

export const OperationsStatsInputSchema = z.object({
  makeDate: z.string().date().describe('Make date at the facility, in YYYY-MM-DD format'),
  facilityId: z.coerce.number().int().positive().describe('Facility ID'),
})
export type OperationsStatsInput = z.infer<typeof OperationsStatsInputSchema>

/*******************************************************************************
 * Pallets
 ******************************************************************************/

export const PALLET_STATUS_OPTIONS = ['empty', 'loading', 'wrapped'] as const
export const PalletStatusSchema = z.enum(PALLET_STATUS_OPTIONS)
export type PalletStatus = z.infer<typeof PalletStatusSchema>

export const PalletInfoSchema = z.object({
  id: z.string(),
  serialNumber: z.string(),
  depotCode: z.string(),
  boxesScanned: z.number(),
  maxBoxes: z.number(),
  cutoffTime: z.string().nullable(),
  status: PalletStatusSchema,
  brand: z.nativeEnum(Brand),
})
export type PalletInfo = z.infer<typeof PalletInfoSchema>

export const PalletListResponseSchema = z.object({
  pallets: z.array(PalletInfoSchema),
})
export type PalletListResponse = z.infer<typeof PalletListResponseSchema>

export const CreatePalletInputSchema = z.object({
  depotCode: z.string(),
  maxCapacity: z.number(),
  amount: z.number().positive().default(1),
  brand: z.nativeEnum(Brand),
})
export type CreatePalletInput = z.infer<typeof CreatePalletInputSchema>

export const PalletDetailSchema = z.object({
  palletId: z.string(),
  depotCode: z.string(),
  serialNumber: z.string(),
  maxCapacity: z.number(),
  status: PalletStatusSchema,
  brand: z.nativeEnum(Brand),
  createdTimeUTC: z.string(),
  updatedTimeUTC: z.string(),
})
export type PalletDetail = z.infer<typeof PalletDetailSchema>

export const GetPalletResponseSchema = z.object({
  palletBoxCreatedTimeUTC: z.string(),
  pallet: PalletDetailSchema,
})
export type GetPalletResponse = z.infer<typeof GetPalletResponseSchema>

export const BoxesByDepotSchema = z.object({
  depotCode: z.string(),
  brand: z.nativeEnum(Brand),
  boxes: z.number().default(0),
})
export type BoxesByDepot = z.infer<typeof BoxesByDepotSchema>

export const GetBoxesByDepotsResponseSchema = z.array(BoxesByDepotSchema)
export type GetBoxesByDepotsResponse = z.infer<typeof GetBoxesByDepotsResponseSchema>

export const PalletDepotInfoSchema = z.object({
  depotCode: BeCoolDepotCode,
  fullPallets: z.coerce.number().int().nonnegative(),
  lastPalletBoxes: z.coerce.number().int().nonnegative(),
  hasBoxes: z.boolean(),
  totalBoxes: z.coerce.number().int().nonnegative(),
  boxesByBrand: z
    .array(
      z.object({
        brand: z.nativeEnum(Brand),
        totalBoxes: z.coerce.number().int().nonnegative(),
      }),
    )
    .default([]),
})

export type PalletDepotInfo = z.infer<typeof PalletDepotInfoSchema>
export const PalletDepotListInputSchema = OperationsStatsInputSchema.extend({
  includeEmptyDepots: z
    .string()
    .transform(val => val === 'true')
    .describe('Include empty depots in the list.'),
})

export type PalletDepotListInput = z.infer<typeof PalletDepotListInputSchema>
export const PalletDepotListResponseSchema = z.object({
  depots: z.array(PalletDepotInfoSchema),
  emptyDepotCount: z.number().int().nonnegative(),
})
export type PalletDepotListResponse = z.infer<typeof PalletDepotListResponseSchema>

// Fulfilment order IDs — reconciliation query
export const GetFulfilmentOrderIdsQuerySchema = z.object({
  facilityId: z.coerce.number().int().positive().describe('Facility ID (1=WP, 2=DS)'),
  makeDate: z.string().date().describe('Make date in YYYY-MM-DD format'),
})

export type GetFulfilmentOrderIdsQuery = z.infer<typeof GetFulfilmentOrderIdsQuerySchema>

export const GetFulfilmentOrderIdsResponseSchema = z.object({
  orderIds: z.array(z.string()).describe('Distinct order IDs with active (non-cancelled) fulfilment jobs'),
})

export type GetFulfilmentOrderIdsResponse = z.infer<typeof GetFulfilmentOrderIdsResponseSchema>

export const GetJobsWithMissingDispatchCutoffQuerySchema = z.object({
  makeDateFrom: z.string().date().describe('Start of make date window (YYYY-MM-DD, Australia/Melbourne calendar date)'),
  makeDateTo: z.string().date().describe('End of make date window (YYYY-MM-DD, Australia/Melbourne calendar date)'),
})

export type GetJobsWithMissingDispatchCutoffQuery = z.infer<typeof GetJobsWithMissingDispatchCutoffQuerySchema>

export const JobWithMissingDispatchCutoffSchema = z.object({
  jobId: z.number().int().positive(),
  orderId: z.string().uuid(),
})

export type JobWithMissingDispatchCutoff = z.infer<typeof JobWithMissingDispatchCutoffSchema>

export const GetJobsWithMissingDispatchCutoffResponseSchema = z.object({
  items: z.array(JobWithMissingDispatchCutoffSchema),
  total: z.number().int().nonnegative(),
})

export type GetJobsWithMissingDispatchCutoffResponse = z.infer<typeof GetJobsWithMissingDispatchCutoffResponseSchema>
